import { useEffect, useRef } from 'react';

/**
 * Global overlay stack — tracks open overlays in mount order.
 * Only the topmost overlay should respond to an Escape keypress.
 */
const overlayStack: symbol[] = [];

/**
 * Hook that registers an overlay in a shared stack and only calls `onClose`
 * when Escape is pressed AND this overlay is the topmost in the stack.
 *
 * This prevents a single Escape press from closing multiple stacked overlays
 * (e.g. a dropdown inside a modal). `onClose` is read from a ref so a new
 * function identity on re-render cannot re-push this overlay above children
 * that are still open.
 */
export function useOverlayEscape(
  open: boolean,
  onClose: () => void,
  enabled = true,
): void {
  const idRef = useRef<symbol | null>(null);
  if (!idRef.current) {
    idRef.current = Symbol('overlay');
  }

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || !enabled) return undefined;

    const id = idRef.current!;
    overlayStack.push(id);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (overlayStack[overlayStack.length - 1] !== id) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      onCloseRef.current();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const idx = overlayStack.indexOf(id);
      if (idx !== -1) overlayStack.splice(idx, 1);
    };
  }, [open, enabled]);
}
